import { SLICE_TAG } from '@/constants';

import { apiSlice } from '../api-slice';
import {
    CustomersReportResponseSchema,
    ExpenseReportResponseSchema,
    InvoiceReportSchema,
    OrderStatsSchema,
    PurchaseSummarySchema,
    SalesOverviewSchema,
    SalesReportSchema,
    SalesSummarySchema,
    StatsSchema,
    SupplierReportResponseSchema,
    TopSellingProductsSchema,
} from './report.schema';

export const dashboardApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getStoreOverview: builder.query({
            query: (params) => {
                return {
                    url: 'reports/dashboard/summary',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return StatsSchema.parse(response).data;
            },
        }),

        getStoreSaleOverview: builder.query({
            query: () => {
                return {
                    url: 'reports/sales',
                    method: 'GET',
                };
            },
            transformResponse: (response) => {
                return SalesOverviewSchema.safeParse(response).data?.data;
            },
        }),

        getOrderStats: builder.query({
            query: (params) => {
                return {
                    url: 'reports/orders/summary',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return OrderStatsSchema.parse(response).data;
            },
        }),

        getSalesReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/sales/all',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return SalesReportSchema.parse(response).data;
            },
        }),

        getSalesReportPdf: builder.query({
            query: (params) => {
                return {
                    url: 'reports/sales/all/pdf',
                    method: 'GET',
                    params,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),

        getInvoiceReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/invoice',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return InvoiceReportSchema.parse(response).data;
            },
        }),

        getCustomerReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/customers',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return CustomersReportResponseSchema.parse(response);
            },
        }),

        getCustomerReportPdf: builder.mutation({
            query: (params) => {
                return {
                    url: 'reports/customers/pdf',
                    method: 'GET',
                    params,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),

        getSupplierReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/suppliers',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return SupplierReportResponseSchema.parse(response);
            },
        }),

        getSupplierReportPdf: builder.query({
            query: (params) => {
                return {
                    url: 'reports/suppliers/pdf',
                    method: 'GET',
                    params,
                    responseHandler: (response) => response.blob(),
                };
            },
        }),

        getTopSellingProducts: builder.query({
            query: (params) => {
                return {
                    url: 'reports/sales/products',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return TopSellingProductsSchema.parse(response);
            },
        }),

        getProfitAndLossReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/profit-and-loss',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.PROFIT_LOSS_REPORT],
        }),

        getExpenseReport: builder.query({
            query: (params) => {
                return {
                    url: 'reports/expenses',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.EXPENSE_REPORT],

            transformResponse(baseQueryReturnValue) {
                return ExpenseReportResponseSchema.parse(baseQueryReturnValue);
            },
        }),

        getSalesSummary: builder.query({
            query: (params) => {
                return {
                    url: 'reports/sales/summary',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return SalesSummarySchema.parse(response);
            },
        }),

        getPurchaseSummary: builder.query({
            query: (params) => {
                return {
                    url: 'reports/purchases/summary',
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => {
                return PurchaseSummarySchema.parse(response);
            },
        }),
    }),
});
export const {
    useGetStoreOverviewQuery,
    useGetStoreSaleOverviewQuery,
    useGetOrderStatsQuery,
    useGetSalesReportQuery,
    useGetInvoiceReportQuery,
    useGetCustomerReportQuery,
    useGetSupplierReportQuery,
    useLazyGetSalesReportPdfQuery,
    useLazyGetSupplierReportPdfQuery,
    useGetCustomerReportPdfMutation,
    useGetTopSellingProductsQuery,
    useGetProfitAndLossReportQuery,
    useGetExpenseReportQuery,

    useGetSalesSummaryQuery,
    useGetPurchaseSummaryQuery,
} = dashboardApi;
